import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Building2, Trees, ClipboardCheck, ArrowRight, Check } from "lucide-react";

const leistungen = [
  {
    title: "Bauunternehmung",
    description: "Bausanierung, Kellerabdichtungen, Tiefbau und Pflasterarbeiten. Qualität, Termintreue und fachliche Kompetenz im Raum Braunschweig.",
    color: "blue",
    icon: Building2,
    link: "/bauunternehmung",
    image: "/images/bau-hero.jpg",
    services: [
      "Bausanierung",
      "Kellerabdichtungen",
      "Kellersanierungen",
      "Tiefbau",
      "Pflasterarbeiten",
      "Grundstücksentwässerung",
    ],
  },
  {
    title: "Galabau",
    description: "Garten- und Landschaftsbau mit Leidenschaft. Wir gestalten Außenanlagen, die beeindrucken – von privaten Gärten bis hin zu großflächigen Projekten.",
    color: "green",
    icon: Trees,
    link: "/galabau",
    image: "/images/galabau-hero.jpg",
    services: [
      "Gartengestaltung & Planung",
      "Pflasterarbeiten & Wegebau",
      "Bepflanzung & Rollrasen",
      "Grünflächenpflege",
    ],
  },
  {
    title: "Sachverständigenbüro",
    description: "Unabhängige Gutachten und fachkundige Beratung. Unsere Sachverständigen liefern fundierte Bewertungen für Immobilien und Bauprojekte.",
    color: "orange",
    icon: ClipboardCheck,
    link: "/sachverstaendiger",
    image: "/images/sv-hero.jpg",
    services: [
      "Immobiliengutachten",
      "Bauschadenbewertung",
      "Wertermittlung",
      "Beweissicherung",
      "Bauüberwachung",
      "Fachberatung & Consulting",
    ],
  },
];

const colorMap = {
  blue: {
    gradientLine: "gradient-blue",
    gradientBg: "gradient-blue-subtle",
    text: "text-brand-blue-light",
    border: "border-brand-blue/20",
    badge: "bg-brand-blue-muted text-brand-blue-light",
    btnVariant: "blue",
    btnOutline: "blue-outline",
    dot: "bg-brand-blue",
  },
  green: {
    gradientLine: "gradient-green",
    gradientBg: "gradient-green-subtle",
    text: "text-brand-green-light",
    border: "border-brand-green/20",
    badge: "bg-brand-green-muted text-brand-green-light",
    btnVariant: "green",
    btnOutline: "green-outline",
    dot: "bg-brand-green",
  },
  orange: {
    gradientLine: "gradient-orange",
    gradientBg: "gradient-orange-subtle",
    text: "text-brand-orange-light",
    border: "border-brand-orange/20",
    badge: "bg-brand-orange-muted text-brand-orange-light",
    btnVariant: "orange",
    btnOutline: "orange-outline",
    dot: "bg-brand-orange",
  },
};

export const LeistungenSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="section-padding bg-background" ref={ref} id="leistungen">
      <div className="container-wide">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-16 max-w-2xl"
        >
          <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
            Unsere Leistungen
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
            Drei Bereiche.
            <span className="text-muted-foreground block">Volle Kompetenz.</span>
          </h2>
          <p className="text-sm md:text-base text-muted-foreground font-body leading-relaxed">
            Die Freitag-Gruppe vereint drei spezialisierte Fachunternehmen. Jeder Bereich bringt eigene Expertise mit – zusammen bilden wir ein unschlagbares Team.
          </p>
        </motion.div>

        {/* Service blocks - each with image + content side by side */}
        <div className="space-y-6">
          {leistungen.map((item, index) => {
            const colors = colorMap[item.color];
            const Icon = item.icon;
            const isReversed = index % 2 !== 0;

            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <Card className="border bg-card overflow-hidden">
                  <div className={`h-1 ${colors.gradientLine}`} />
                  <div className={`grid grid-cols-1 lg:grid-cols-2 ${isReversed ? '' : ''}`}>
                    {/* Image side */}
                    <div
                      className={`relative min-h-[280px] lg:min-h-[400px] bg-cover bg-center ${isReversed ? 'lg:order-2' : ''}`}
                      style={{ backgroundImage: `url(${item.image})` }}
                    >
                      <div className="absolute inset-0 bg-background/40" />
                      <div className="absolute bottom-6 left-6 right-6">
                        <div className={`inline-flex items-center gap-2 px-4 py-2 ${colors.gradientBg} border ${colors.border} backdrop-blur-sm`}>
                          <Icon className={`w-4 h-4 ${colors.text}`} />
                          <span className={`text-xs font-heading uppercase tracking-wider ${colors.text}`}>
                            Freitag {item.title}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Content side */}
                    <CardContent className="p-8 lg:p-12 flex flex-col">
                      <h3 className={`font-heading font-bold text-2xl lg:text-3xl mb-4 ${colors.text}`}>
                        {item.title}
                      </h3>

                      <p className="text-sm text-muted-foreground font-body leading-relaxed mb-6">
                        {item.description}
                      </p>

                      {/* Services grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                        {item.services.map((service) => (
                          <div key={service} className="flex items-center gap-2.5">
                            <Check className={`w-4 h-4 ${colors.text} shrink-0`} />
                            <span className="text-sm text-foreground/80 font-body">{service}</span>
                          </div>
                        ))}
                      </div>

                      <div className="flex flex-wrap gap-3 mt-auto">
                        <Button variant={colors.btnVariant} size="lg" asChild>
                          <Link to={item.link}>
                            Mehr erfahren
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        </Button>
                        <Button variant={colors.btnOutline} size="lg" asChild>
                          <Link to={`${item.link}#kontakt`}>
                            Anfrage stellen
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
